import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';
import { apiFetch, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { operatorListSchema, operatorSchema, createOperatorResponseSchema } from '@/schemas/users';
import type { Operator, OperatorRole, CreateOperator, InviteOperator } from '@/schemas/users';
import UsersTable from '@/components/app/users-table';
import CreateUserDialog, { type CreateUserFormValues } from '@/components/app/create-user-dialog';
import InviteLinkDialog from '@/components/app/invite-link-dialog';
import ChangeRoleDialog from '@/components/app/change-role-dialog';
import ResetPasswordDialog from '@/components/app/reset-password-dialog';
import ConfirmDestructiveDialog from '@/components/app/confirm-destructive-dialog';

async function fetchUsers() {
  const resp = await apiFetch('/users');
  const json = await resp.json();
  return operatorListSchema.parse(json);
}

async function createUser(data: CreateOperator | InviteOperator) {
  const resp = await apiFetch('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return createOperatorResponseSchema.parse(await resp.json());
}

async function resendInvite(id: string) {
  const resp = await apiFetch(`/users/${encodeURIComponent(id)}/resend-invite`, {
    method: 'POST',
  });
  return createOperatorResponseSchema.parse(await resp.json());
}

async function patchUser(
  id: string,
  body: { role?: OperatorRole; password?: string; active?: boolean }
) {
  const resp = await apiFetch(`/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return operatorSchema.parse(await resp.json());
}

/** Pull the server's human message out of an ApiError raised from a JSON error body. */
function serverMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.message) as { error?: string };
      if (parsed.error) return parsed.error;
    } catch {
      return err.message;
    }
  }
  return '';
}

export default function OperatorsPage() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  const [createOpen, setCreateOpen] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);
  const [createInviteUrl, setCreateInviteUrl] = React.useState<string | null>(null);
  const [resendInviteUrl, setResendInviteUrl] = React.useState<string | null>(null);
  const [roleTarget, setRoleTarget] = React.useState<Operator | null>(null);
  const [resetTarget, setResetTarget] = React.useState<Operator | null>(null);
  const [deactivateTarget, setDeactivateTarget] = React.useState<Operator | null>(null);

  const query = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
    staleTime: 30_000,
  });

  /** 409 from the backend → specific i18n'd toast; anything else → generic. */
  const toastConflictOrGeneric = (err: unknown, genericKey: string) => {
    if (err instanceof ApiError && err.status === 409) {
      const msg = serverMessage(err);
      if (/yourself/i.test(msg)) return toast.error(t('operators.toastSelfConflict'));
      if (/last active admin/i.test(msg)) return toast.error(t('operators.toastLastAdminConflict'));
      if (/username already taken/i.test(msg))
        return toast.error(t('operators.toastUsernameTaken'));
    }
    return toast.error(t(genericKey));
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const createMutation = useMutation({
    mutationFn: createUser,
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        /username already taken/i.test(serverMessage(err))
      ) {
        setCreateError(t('operators.toastUsernameTaken'));
      } else {
        setCreateError(t('operators.toastCreateError'));
      }
    },
    onSuccess: (data) => {
      const invite = data.invite;
      if (invite && !invite.delivered && invite.inviteUrl) {
        // SMTP degrade: keep the dialog open and hand the one-time link to the admin.
        setCreateInviteUrl(invite.inviteUrl);
      } else {
        setCreateOpen(false);
        setCreateError(null);
        toast.success(
          invite?.delivered ? t('operators.toastInviteSent') : t('operators.toastCreated')
        );
      }
      invalidate();
    },
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => resendInvite(id),
    onError: (err) => {
      if (err instanceof ApiError && err.status === 429) {
        toast.error(t('operators.toastResendRateLimited'));
      } else {
        toast.error(t('operators.toastResendError'));
      }
    },
    onSuccess: (data) => {
      const invite = data.invite;
      if (invite && !invite.delivered && invite.inviteUrl) {
        setResendInviteUrl(invite.inviteUrl);
      } else {
        toast.success(t('operators.toastInviteResent'));
      }
      invalidate();
    },
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: OperatorRole }) => patchUser(id, { role }),
    onError: (err) => toastConflictOrGeneric(err, 'operators.toastUpdateError'),
    onSuccess: () => {
      setRoleTarget(null);
      toast.success(t('operators.toastRoleChanged'));
      invalidate();
    },
  });

  const resetMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => patchUser(id, { password }),
    onError: (err) => toastConflictOrGeneric(err, 'operators.toastUpdateError'),
    onSuccess: () => {
      setResetTarget(null);
      toast.success(t('operators.toastPasswordReset'));
      invalidate();
    },
  });

  const activeMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => patchUser(id, { active }),
    onError: (err) => toastConflictOrGeneric(err, 'operators.toastUpdateError'),
    onSuccess: (_data, vars) => {
      setDeactivateTarget(null);
      toast.success(
        vars.active ? t('operators.toastReactivated') : t('operators.toastDeactivated')
      );
      invalidate();
    },
  });

  const isEmpty = !query.isLoading && query.data?.length === 0;
  const isError = query.isError;

  return (
    <div className="mx-auto w-full max-w-[1120px]">
      {/* Page head */}
      <div className="flex items-start justify-between gap-8">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-foreground">
            {t('operators.title')}
          </h1>
          <p className="mt-2.5 max-w-[52ch] font-serif text-[17.5px] italic leading-relaxed text-muted-foreground">
            {t('operators.subtitle')}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="mt-2.5 shrink-0">
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('operators.addUser')}
        </Button>
      </div>

      <div className="mt-8">
        {isError ? (
          <div className="flex items-center justify-between rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">{t('operators.loadError')}</p>
            <Button variant="outline" size="sm" onClick={() => query.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        ) : isEmpty ? (
          <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
            <h2 className="font-serif text-2xl italic tracking-tight text-foreground">
              {t('operators.empty')}
            </h2>
            <p className="max-w-[44ch] text-center text-[14.5px] text-muted-foreground">
              {t('operators.emptyHint')}
            </p>
            <Button onClick={() => setCreateOpen(true)} className="mt-1">
              {t('operators.emptyCta')}
            </Button>
          </div>
        ) : (
          <UsersTable
            data={query.data ?? []}
            isLoading={query.isLoading}
            currentUserId={currentUser?.id}
            onChangeRole={setRoleTarget}
            onResetPassword={setResetTarget}
            onDeactivate={setDeactivateTarget}
            onReactivate={(u) => activeMutation.mutate({ id: u.id, active: true })}
            onResendInvite={(u) => resendMutation.mutate(u.id)}
          />
        )}
      </div>

      <CreateUserDialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) {
            setCreateError(null);
            setCreateInviteUrl(null);
          }
        }}
        onSubmit={(values: CreateUserFormValues) => {
          setCreateError(null);
          const payload =
            values.mode === 'invite'
              ? { username: values.username, email: values.email!, role: values.role }
              : { username: values.username, password: values.password!, role: values.role };
          createMutation.mutate(payload);
        }}
        isLoading={createMutation.isPending}
        serverError={createError}
        inviteUrl={createInviteUrl}
      />

      <InviteLinkDialog
        inviteUrl={resendInviteUrl}
        onOpenChange={(open) => {
          if (!open) setResendInviteUrl(null);
        }}
      />

      <ChangeRoleDialog
        user={roleTarget}
        open={!!roleTarget}
        onOpenChange={(open) => {
          if (!open) setRoleTarget(null);
        }}
        onSubmit={(role) => roleTarget && roleMutation.mutate({ id: roleTarget.id, role })}
        isLoading={roleMutation.isPending}
      />

      <ResetPasswordDialog
        user={resetTarget}
        open={!!resetTarget}
        onOpenChange={(open) => {
          if (!open) setResetTarget(null);
        }}
        onSubmit={(password) =>
          resetTarget && resetMutation.mutate({ id: resetTarget.id, password })
        }
        isLoading={resetMutation.isPending}
      />

      {deactivateTarget && (
        <ConfirmDestructiveDialog
          open={!!deactivateTarget}
          onOpenChange={(open) => {
            if (!open) setDeactivateTarget(null);
          }}
          title={t('operators.deactivateDialog.title')}
          description={
            <Trans
              i18nKey="operators.deactivateDialog.description"
              values={{ username: deactivateTarget.username }}
              components={{ strong: <strong /> }}
            />
          }
          confirmValue={deactivateTarget.username}
          confirmLabel={t('operators.deactivateDialog.confirm')}
          onConfirm={() => activeMutation.mutate({ id: deactivateTarget.id, active: false })}
          isLoading={activeMutation.isPending}
        />
      )}
    </div>
  );
}

export const handle = { crumb: 'Operators' };
